const PACKET_SIZE = 188;
const HEVC_STREAM_TYPE = 0x24;
const VIDEO_STREAM_TYPES = new Set([0x24, 0x1b, 0x10, 0x1e, 0x20, 0x21]);

// HEVC NAL unit types that indicate a clean random-access point (IDR/CRA/BLA).
// We only resume feeding the decoder once we see one of these after a drop,
// otherwise the decoder keeps concealing/propagating corruption until the
// next natural keyframe (which is what produced the blocky OSD text).
const HEVC_KEYFRAME_NAL_TYPES = new Set([16, 17, 18, 19, 20, 21]);

function readPid(packet, offset) {
  return ((packet[offset + 1] & 0x1f) << 8) | packet[offset + 2];
}

function parsePat(payload) {
  let offset = 1 + (payload[0] || 0);
  if (offset + 8 > payload.length) return null;
  const sectionLength = ((payload[offset + 1] & 0x0f) << 8) | payload[offset + 2];
  const end = Math.min(payload.length, offset + 3 + sectionLength - 4);
  offset += 8;
  while (offset + 4 <= end) {
    const program = (payload[offset] << 8) | payload[offset + 1];
    const pid = ((payload[offset + 2] & 0x1f) << 8) | payload[offset + 3];
    if (program !== 0) return pid;
    offset += 4;
  }
  return null;
}

function parsePmt(payload) {
  let offset = 1 + (payload[0] || 0);
  if (offset + 12 > payload.length) return null;
  const sectionLength = ((payload[offset + 1] & 0x0f) << 8) | payload[offset + 2];
  const end = Math.min(payload.length, offset + 3 + sectionLength - 4);
  const programInfoLength = ((payload[offset + 10] & 0x0f) << 8) | payload[offset + 11];
  offset += 12 + programInfoLength;
  while (offset + 5 <= end) {
    const streamType = payload[offset];
    const pid = ((payload[offset + 1] & 0x1f) << 8) | payload[offset + 2];
    const esInfoLength = ((payload[offset + 3] & 0x0f) << 8) | payload[offset + 4];
    if (streamType === HEVC_STREAM_TYPE) return pid;
    offset += 5 + esInfoLength;
  }
  offset = 1 + (payload[0] || 0);
  offset += 12 + programInfoLength;
  while (offset + 5 <= end) {
    const streamType = payload[offset];
    const pid = ((payload[offset + 1] & 0x1f) << 8) | payload[offset + 2];
    const esInfoLength = ((payload[offset + 3] & 0x0f) << 8) | payload[offset + 4];
    if (VIDEO_STREAM_TYPES.has(streamType)) return pid;
    offset += 5 + esInfoLength;
  }
  return null;
}

function pesPayload(pes) {
  if (pes.length < 9 || pes[0] !== 0x00 || pes[1] !== 0x00 || pes[2] !== 0x01) {
    return pes;
  }
  const headerLength = pes[8];
  const start = 9 + headerLength;
  const packetLength = (pes[4] << 8) | pes[5];
  const end = packetLength === 0 ? pes.length : Math.min(pes.length, 6 + packetLength);
  if (start >= end) return null;
  return pes.subarray(start, end);
}

// Scans an HEVC access unit (Annex-B, start-code delimited) for a NAL unit
// type that marks a clean random access point.
function containsKeyframeNal(accessUnit) {
  const length = accessUnit.length;
  for (let index = 0; index + 4 < length; index += 1) {
    if (
      accessUnit[index] === 0x00 &&
      accessUnit[index + 1] === 0x00 &&
      ((accessUnit[index + 2] === 0x01) ||
        (accessUnit[index + 2] === 0x00 && accessUnit[index + 3] === 0x01))
    ) {
      const headerOffset = accessUnit[index + 2] === 0x01 ? index + 3 : index + 4;
      if (headerOffset >= length) continue;
      const nalType = (accessUnit[headerOffset] >> 1) & 0x3f;
      if (HEVC_KEYFRAME_NAL_TYPES.has(nalType)) return true;
    }
  }
  return false;
}

function concat(parts) {
  const length = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(length);
  let offset = 0;
  parts.forEach((part) => {
    out.set(part, offset);
    offset += part.length;
  });
  return out;
}

export function createMpegTsDemuxer(onAccessUnit) {
  let pending = new Uint8Array(0);
  let pmtPid = null;
  let videoPid = null;
  let pes = [];

  // Continuity-counter tracking (per ISO/IEC 13818-1). Any unexpected jump
  // means we lost/duplicated a TS packet on this PID.
  let lastContinuity = null;

  // Once a discontinuity corrupts the in-flight PES/access-unit, we drop
  // everything until the next keyframe instead of handing the decoder a
  // broken frame (which is what caused the blocky/torn OSD text: a corrupt
  // I-frame gets error-concealed and that corruption then propagates
  // through every P-frame referencing it until the next IDR).
  let waitingForKeyframe = false;

  const discardPes = () => {
    pes = [];
  };

  const flushPes = () => {
    if (!pes.length) return;
    const combined = concat(pes);
    pes = [];
    const payload = pesPayload(combined);
    if (!payload || !payload.length) return;

    if (waitingForKeyframe) {
      if (!containsKeyframeNal(payload)) return;
      waitingForKeyframe = false;
    }

    onAccessUnit(payload);
  };

  const noteDiscontinuity = () => {
    discardPes();
    waitingForKeyframe = true;
    lastContinuity = null;
  };

  const pushPacket = (packet) => {
    if (packet[0] !== 0x47) return;

    if ((packet[1] & 0x80) !== 0) {
      if (videoPid !== null) noteDiscontinuity();
      return;
    }

    const pid = readPid(packet, 0);
    const payloadStart = (packet[1] & 0x40) !== 0;
    const adaptation = (packet[3] & 0x30) >> 4;
    const continuity = packet[3] & 0x0f;
    const hasPayload = adaptation === 1 || adaptation === 3;
    let discontinuityIndicator = false;

    let offset = 4;
    if (adaptation === 2 || adaptation === 3) {
      const adaptationLength = packet[4] || 0;
      if (adaptationLength > 0) discontinuityIndicator = (packet[5] & 0x80) !== 0;
      if (adaptation === 2) return;
      offset += 1 + adaptationLength;
      if (offset >= PACKET_SIZE) return;
    }
    const payload = packet.subarray(offset);

    if (pid === 0 && payloadStart) {
      pmtPid = parsePat(payload);
      return;
    }
    if (pmtPid !== null && pid === pmtPid && payloadStart) {
      const parsedVideoPid = parsePmt(payload);
      if (parsedVideoPid !== null && parsedVideoPid !== videoPid) {
        discardPes();
        lastContinuity = null;
        waitingForKeyframe = false;
        videoPid = parsedVideoPid;
      }
      return;
    }
    if (videoPid === null || pid !== videoPid) return;

    if (discontinuityIndicator) lastContinuity = null;

    if (hasPayload && lastContinuity !== null) {
      if (continuity === lastContinuity) return;
      if (continuity !== ((lastContinuity + 1) & 0x0f)) {
        noteDiscontinuity();
        if (!payloadStart) return;
      }
    }
    if (hasPayload) lastContinuity = continuity;

    if (payloadStart) flushPes();
    pes.push(payload);
  };

  const resetParser = () => {
    pending = new Uint8Array(0);
    pmtPid = null;
    videoPid = null;
    pes = [];
    lastContinuity = null;
    waitingForKeyframe = true;
  };

  const push = (chunk) => {
    if (!chunk || !chunk.length) return;
    const data = pending.length ? concat([pending, chunk]) : chunk;
    let offset = 0;
    if (data[0] !== 0x47) {
      const sync = data.indexOf(0x47);
      if (sync < 0) {
        pending = new Uint8Array(0);
        return;
      }
      offset = sync;
    }
    while (offset + PACKET_SIZE <= data.length) {
      if (data[offset] !== 0x47) {
        const sync = data.indexOf(0x47, offset + 1);
        if (sync < 0) {
          pending = new Uint8Array(0);
          return;
        }
        offset = sync;
        continue;
      }
      pushPacket(data.subarray(offset, offset + PACKET_SIZE));
      offset += PACKET_SIZE;
    }
    pending = data.subarray(offset);
  };

  return {
    push,
    flush() {
      flushPes();
    },
    resync() {
      pending = new Uint8Array(0);
      pes = [];
      lastContinuity = null;
      waitingForKeyframe = true;
    },
    // Keep the newest keyframe and the frames after it. Older buffered
    // video is discarded so a tab that was in the background does not
    // replay those seconds at decode speed.
    jumpToLive(chunks) {
      const parts = (chunks || []).filter((chunk) => chunk && chunk.length);
      if (!parts.length) return false;
      const data = parts.length === 1 ? parts[0] : concat(parts);
      let origin = 0;
      if (data[0] !== 0x47) {
        origin = data.indexOf(0x47);
        if (origin < 0) return false;
      }

      let scanPmtPid = null;
      let scanVideoPid = null;
      let lastPat = null;
      let lastPmt = null;
      let pesParts = [];
      let pesStart = -1;
      let lastKeyOffset = -1;

      const finishPes = () => {
        if (pesStart >= 0 && pesParts.length) {
          const payload = pesPayload(concat(pesParts));
          if (payload && containsKeyframeNal(payload)) lastKeyOffset = pesStart;
        }
        pesParts = [];
        pesStart = -1;
      };

      for (let offset = origin; offset + PACKET_SIZE <= data.length; offset += PACKET_SIZE) {
        const packet = data.subarray(offset, offset + PACKET_SIZE);
        if (packet[0] !== 0x47) continue;
        if ((packet[1] & 0x80) !== 0) continue;
        const pid = readPid(packet, 0);
        const payloadStart = (packet[1] & 0x40) !== 0;
        const adaptation = (packet[3] & 0x30) >> 4;
        let payloadOffset = 4;
        if (adaptation === 2 || adaptation === 3) {
          const adaptationLength = packet[4] || 0;
          if (adaptation === 2) continue;
          payloadOffset += 1 + adaptationLength;
          if (payloadOffset >= PACKET_SIZE) continue;
        } else if (adaptation !== 1) {
          continue;
        }
        const payload = packet.subarray(payloadOffset);
        if (pid === 0 && payloadStart) {
          lastPat = packet.slice();
          scanPmtPid = parsePat(payload);
          continue;
        }
        if (scanPmtPid !== null && pid === scanPmtPid && payloadStart) {
          lastPmt = packet.slice();
          const parsedVideoPid = parsePmt(payload);
          if (parsedVideoPid !== null) scanVideoPid = parsedVideoPid;
          continue;
        }
        if (scanVideoPid === null || pid !== scanVideoPid) continue;
        if (payloadStart) {
          finishPes();
          pesStart = offset;
          pesParts = [payload];
          continue;
        }
        if (pesStart < 0) pesStart = offset;
        pesParts.push(payload);
      }
      finishPes();
      if (lastKeyOffset < 0) return false;

      resetParser();
      const live = [lastPat, lastPmt, data.subarray(lastKeyOffset)].filter(Boolean);
      push(concat(live));
      flushPes();
      return true;
    },
  };
}